// src/features/dashboard/components/SalesChart.tsx
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { GlassCard } from '../../../app/components/ui/GlassCard';
import { ChartCardSkeleton } from './ui/Skeleton';
import { formatCompactToman, formatToman } from '../utils/formatters';
import type { DailySalesPoint } from '../types/dashboard.types';

interface SalesChartProps {
  data: DailySalesPoint[];
  isInitialLoading: boolean;
}

function SalesTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="vania-chart-tooltip">
      <span className="text-xs text-[var(--vania-text-tertiary,#6b7280)]">{label}</span>
      <span className="text-sm font-bold text-[var(--vania-text-primary,#f5f6fa)]">
        {formatToman(payload[0].value)}
      </span>
    </div>
  );
}

export function SalesChart({ data, isInitialLoading }: SalesChartProps) {
  if (isInitialLoading) return <ChartCardSkeleton />;

  const total = data.reduce((sum, point) => sum + point.amount, 0);

  return (
    <GlassCard noPadding className="vania-chart-card" data-animate="fade-in">
      <div className="flex items-center justify-between p-5 sm:p-6 pb-0">
        <div>
          <h3 className="text-sm font-semibold text-[var(--vania-text-primary,#f5f6fa)]">
            فروش روزانه
          </h3>
          <p className="text-xs text-[var(--vania-text-tertiary,#6b7280)] mt-1">۳۰ روز گذشته</p>
        </div>
        <span className="vania-badge vania-badge--teal">
          <TrendingUp size={12} strokeWidth={2.5} />
          {formatCompactToman(total)}
        </span>
      </div>

      <div className="h-64 w-full p-4 sm:p-5 pt-2" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="vania-sales-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--vania-accent-violet, #7c6cff)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="var(--vania-accent-violet, #7c6cff)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 6"
              stroke="var(--vania-border-glass, rgba(255,255,255,0.08))"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--vania-text-tertiary, #6b7280)', fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              interval={4}
            />
            <YAxis
              tick={{ fill: 'var(--vania-text-tertiary, #6b7280)', fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => formatCompactToman(v).replace(' تومان', '')}
              width={44}
            />
            <Tooltip content={<SalesTooltip />} cursor={{ stroke: 'var(--vania-accent-violet, #7c6cff)', strokeWidth: 1, strokeDasharray: '4 4' }} />
            <Area
              type="monotone"
              dataKey="amount"
              stroke="var(--vania-accent-violet, #7c6cff)"
              strokeWidth={2.5}
              fill="url(#vania-sales-gradient)"
              activeDot={{ r: 4, fill: 'var(--vania-accent-violet, #7c6cff)', strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </GlassCard>
  );
}
